import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, FileText, Download } from "lucide-react";
import { useState } from "react";
import Packshot from "@/components/Packshot";
import { downloadProductLiterature } from "@/lib/pdf";

const overlayFade = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const innerSlide = {
  initial: { y: 40, opacity: 0, rotateX: -6 },
  animate: { y: 0, opacity: 1, rotateX: 0 },
  exit: { y: 30, opacity: 0 },
  transition: { type: "spring", stiffness: 200, damping: 26 },
};

function ProductSummary({ product, onOpenLiterature }) {
  return (
    <>
      <section data-testid="product-modal-composition">
        <p className="eyebrow">Composition</p>
        <p>{product.composition}</p>
      </section>
      <section data-testid="product-modal-indications">
        <p className="eyebrow">Indications</p>
        <p>{product.indications}</p>
      </section>
      <section data-testid="product-modal-dosage">
        <p className="eyebrow">Dosage & administration</p>
        <p>{product.dosage}</p>
      </section>
      <div className="product-modal-actions">
        <button
          className="literature-btn"
          onClick={onOpenLiterature}
          data-testid="view-literature-button"
        >
          <FileText size={16} /> View literature
        </button>
        <button
          className="literature-btn secondary"
          onClick={() => downloadProductLiterature(product)}
          data-testid="download-pdf-button"
          type="button"
        >
          <Download size={16} /> Download PDF
        </button>
        <a
          href={`mailto:info@nawanlab.com?subject=${encodeURIComponent("Product enquiry - " + product.name)}`}
          className="literature-btn secondary"
          data-testid="product-enquiry-link"
        >
          Enquire <ArrowUpRight size={16} />
        </a>
      </div>
    </>
  );
}

function LiteraturePanel({ product, onBack }) {
  return (
    <div className="literature-panel" data-testid="literature-panel">
      <button
        className="literature-back"
        onClick={onBack}
        data-testid="literature-back-button"
      >
        ← Back to summary
      </button>
      <p className="eyebrow">Product literature</p>
      <h4>{product.literature}</h4>
      <div className="literature-sheet">
        <div className="literature-sheet-header">
          <span className="literature-mark">{product.mark}</span>
          <div>
            <b>{product.name}</b>
            <small>{product.generic} · {product.form}</small>
          </div>
        </div>
        <dl className="literature-sheet-body">
          <dt>Composition</dt>
          <dd>{product.composition}</dd>
          <dt>Therapeutic indication</dt>
          <dd>{product.indications}</dd>
          <dt>Recommended dosage</dt>
          <dd>{product.dosage}</dd>
          <dt>Storage</dt>
          <dd>Store below 30°C in a dry place, protected from light. Keep out of reach of children.</dd>
          <dt>Manufactured by</dt>
          <dd>Nawan Pharmaceutical Laboratories (Pvt.) Ltd., 136–138, Sector 15, Korangi Industrial Area, Karachi.</dd>
        </dl>
      </div>
      <button
        onClick={() => downloadProductLiterature(product)}
        className="literature-btn"
        data-testid="download-full-pdf"
        type="button"
      >
        <Download size={16} /> Download pack insert (PDF)
      </button>
    </div>
  );
}

export default function ProductModal({ product, onClose }) {
  const [showLiterature, setShowLiterature] = useState(false);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          className="product-modal"
          role="dialog"
          aria-modal="true"
          data-testid="product-modal"
          {...overlayFade}
          onClick={onClose}
        >
          <motion.div
            className="product-modal-inner"
            {...innerSlide}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="product-modal-close"
              onClick={onClose}
              data-testid="product-modal-close"
              aria-label="Close product details"
            >
              <X size={18} />
            </button>

            <div className="product-modal-visual">
              <Packshot product={product} size="modal" />
              <p className="eyebrow" data-testid="product-modal-division">{product.division}</p>
              <h3 data-testid="product-modal-name">{product.name}</h3>
              <p className="product-modal-generic" data-testid="product-modal-generic">
                {product.generic}
              </p>
              <div className="product-modal-form" data-testid="product-modal-form">
                <span>Form</span>
                <b>{product.form}</b>
              </div>
            </div>

            <div className="product-modal-body">
              {showLiterature ? (
                <LiteraturePanel product={product} onBack={() => setShowLiterature(false)} />
              ) : (
                <ProductSummary product={product} onOpenLiterature={() => setShowLiterature(true)} />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
